import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { RouterProvider, createRouter } from "@tanstack/react-router";

import { routeTree } from "./routeTree.gen";
import { AppErrorScreen } from "./components/AppErrorScreen";
import { loadAuthConfig } from "./lib/auth";
import { recordScreen } from "./lib/screenTrail";
import "./i18n/index";
import "./styles/globals.css";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

const router = createRouter({
  routeTree,
  context: { queryClient },
  defaultPreload: "intent",
  // Without these two the router falls back to its own English "Something went
  // wrong" on a blank page, with no way back — and since the SPA owns the whole
  // window, no browser chrome to escape with either.
  defaultErrorComponent: ({ error }) => <AppErrorScreen error={error} />,
  defaultNotFoundComponent: () => <AppErrorScreen notFound />,
});

// Every screen in this tab's history, for the link back top left on a card (lib/screenTrail).
router.subscribe("onResolved", ({ toLocation }) => recordScreen(toLocation));

// Register the router instance for type-safety across the app.
declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}

// Resolve the auth mode from the API (single source of truth) before the first
// render, so the login screen and the API client agree with the server.
loadAuthConfig().finally(() => {
  createRoot(document.getElementById("root")!).render(
    <StrictMode>
      <QueryClientProvider client={queryClient}>
        <RouterProvider router={router} />
      </QueryClientProvider>
    </StrictMode>,
  );
});
